/**
 * AndroidInstanceMessenger - android multi-instance IPC surface
 * (android-multi-instance-host Phase 3; extracted from PlayjectorService in
 * Phase 8, review fix A5).
 *
 * Owns the android-side messaging state and correlation:
 *  - `getInstanceCapabilities` query/reply coalescing (Phase 2);
 *  - `launchAndroidInstances` (single processLaunch per campaign) plus the
 *    streamId registration that gates reply routing;
 *  - per-instance `instanceLaunched`/`instanceClosed` notification fan-out;
 *  - `restartInstance` request/reply correlation keyed by
 *    `${streamId}:${gameInstanceIndex}`.
 *
 * `PlayjectorService` keeps one `mainMessage` tap and delegates the android
 * branches here, so the `IPlayjectorService` surface is unchanged for callers.
 */

import {
  generators,
  type ProcessLaunchPackageAndroid,
} from '@playcastdotio/Messaging';
import type {
  InstanceCapabilities,
  InstanceClosedEvent,
  InstanceLaunchedEvent,
} from './interfaces/IPlayjectorService';
import { ipcSend } from '../../api/messaging/protocols/ipc';
import { authState } from '../../api/stores/shared';

/** Structural mainMessage envelope the service's IPC tap forwards. */
export type AndroidMainMessage = {
  header: { target: string; action: string; isReply?: boolean };
  body: { message: unknown };
};

/**
 * How long to wait for a `processCapabilitiesReplyMSG` from Playjector before
 * rejecting the in-flight `getInstanceCapabilities` promise. The reply is a
 * cheap local read (provisioned VM enumeration), so 10s is generous; a timeout
 * typically means an old Playjector build without the capabilities schema —
 * callers treat that as "capabilities unknown", never as a hard failure.
 */
const INSTANCE_CAPABILITIES_REPLY_TIMEOUT_MS = 10_000;

/**
 * How long to wait for a `processRestartInstanceReplyMSG` before rejecting the
 * in-flight `restartInstance` promise. A restart reuses an already-provisioned
 * VM, so 60s is generous; a timeout means the C++ side never answered (the
 * caller's recovery policy — Phase 4 — decides what to do next).
 */
const RESTART_INSTANCE_REPLY_TIMEOUT_MS = 60_000;

/** Pending `restartInstance` call, correlated by `${streamId}:${gameInstanceIndex}`. */
interface RestartInstanceWaiter {
  resolve(result: string): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Pending `getInstanceCapabilities` query. The capabilities reply carries no
 * correlation id, so only one query can be in flight at a time and concurrent
 * callers coalesce onto the shared `promise`.
 */
interface CapabilitiesQueryWaiter {
  promise: Promise<InstanceCapabilities>;
  resolve(value: InstanceCapabilities): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export class AndroidInstanceMessenger {
  // Launch-notification subscriptions plus the stream-id registration that
  // gates reply routing (separate from the legacy pavilionLaunchReply
  // correlation).
  private instanceLaunchedListeners: Array<(event: InstanceLaunchedEvent) => void> = [];
  private instanceClosedListeners: Array<(event: InstanceClosedEvent) => void> = [];
  private readonly registeredAndroidStreamIds = new Set<string>();
  private readonly pendingInstanceRestarts = new Map<string, RestartInstanceWaiter>();
  private pendingCapabilitiesQuery: CapabilitiesQueryWaiter | null = null;

  constructor(
    private readonly isAvailable: () => boolean,
  ) {}

  /**
   * Handle the android branches of the service's single `mainMessage` tap:
   * per-instance launch/closed notifications, restart replies, and
   * capabilities replies. Other messages are ignored (the service owns them).
   */
  public handleMainMessage(message: AndroidMainMessage): void {
    if (message.header.target !== 'process') return;
    if (message.header.action === 'capabilities' && message.header.isReply) {
      this.notifyInstanceCapabilitiesReply(message.body.message as InstanceCapabilities);
      return;
    }
    // Per-instance launch notifications and restart replies. Routing is keyed
    // by registered streamId — separate registration from the legacy
    // pavilionLaunchReply correlation, which stays untouched.
    if (message.header.action === 'instanceLaunched' && message.header.isReply) {
      this.routeInstanceLaunched(message.body.message as InstanceLaunchedEvent);
      return;
    }
    if (message.header.action === 'instanceClosed' && message.header.isReply) {
      this.routeInstanceClosed(message.body.message as InstanceClosedEvent);
      return;
    }
    if (message.header.action === 'restartInstance' && message.header.isReply) {
      this.notifyRestartInstanceReply(
        message.body.message as { streamId: string; gameInstanceIndex: number; result: string },
      );
    }
  }

  /**
   * Query the machine's multi-instance capabilities
   * (`processCapabilitiesQueryMSG` → `processCapabilitiesReplyMSG`).
   *
   * The reply carries no correlation id, so only one query can be in flight:
   * concurrent calls coalesce onto the same promise. Rejects when Playjector
   * is unavailable (non-Electron) or when no reply arrives within
   * `INSTANCE_CAPABILITIES_REPLY_TIMEOUT_MS` — e.g. an old Playjector build
   * without the capabilities schema. Callers treat rejection as "unknown",
   * never as a zero ceiling.
   */
  public async getInstanceCapabilities(): Promise<InstanceCapabilities> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }

    if (this.pendingCapabilitiesQuery) {
      return this.pendingCapabilitiesQuery.promise;
    }

    let resolveQuery!: (value: InstanceCapabilities) => void;
    let rejectQuery!: (reason: Error) => void;
    const promise = new Promise<InstanceCapabilities>((resolve, reject) => {
      resolveQuery = resolve;
      rejectQuery = reject;
    });
    const timeoutId = setTimeout(() => {
      this.pendingCapabilitiesQuery = null;
      rejectQuery(new Error(
        `Instance capabilities reply timed out after ${INSTANCE_CAPABILITIES_REPLY_TIMEOUT_MS}ms`,
      ));
    }, INSTANCE_CAPABILITIES_REPLY_TIMEOUT_MS);
    this.pendingCapabilitiesQuery = { promise, resolve: resolveQuery, reject: rejectQuery, timeoutId };

    ipcSend(generators.process.processCapabilitiesQuery(authState.currentState.hostAuthId));
    return promise;
  }

  /**
   * Resolve the in-flight capabilities query with a
   * `processCapabilitiesReplyMSG` payload. Called from the renderer-side IPC
   * tap (PlayjectorService constructor) when Electron forwards the reply.
   */
  public notifyInstanceCapabilitiesReply(reply: InstanceCapabilities): void {
    const waiter = this.pendingCapabilitiesQuery;
    if (!waiter) {
      console.warn('[PlayjectorService] Instance capabilities reply with no pending query — dropping');
      return;
    }
    this.pendingCapabilitiesQuery = null;
    clearTimeout(waiter.timeoutId);
    waiter.resolve({
      provisionedInstanceCount: reply.provisionedInstanceCount,
      maxGameInstances: reply.maxGameInstances,
    });
  }

  /**
   * Launch N android game instances from one launch package: sends a single
   * `processLaunchMSG` whose `launchPackage` is the android package and
   * registers the package's `streamId` so `instanceLaunched`/`instanceClosed`
   * messages for that stream route to subscribers. Per-instance results
   * arrive as `processInstanceLaunchedMSG` notifications — consume them via
   * `onInstanceLaunched`.
   */
  public async launchAndroidInstances(pkg: ProcessLaunchPackageAndroid): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    console.log('[PlayjectorService] 🎮 Launching android instances via processLaunch IPC:', {
      streamId: pkg.streamId,
      package: pkg.package,
      instanceCount: pkg.instanceCount,
    });
    this.registeredAndroidStreamIds.add(pkg.streamId);
    ipcSend(
      generators.process.processLaunch(
        authState.currentState.hostAuthId,
        'pavilion',
        pkg,
      ),
    );
  }

  /**
   * Stop routing `instanceLaunched`/`instanceClosed` notifications for a
   * torn-down android stream (Phase 8, review fix S1): a late event from a
   * previous campaign's stream must never reach the new campaign's consumers.
   */
  public unregisterAndroidStream(streamId: string): void {
    if (this.registeredAndroidStreamIds.delete(streamId)) {
      console.log(`[PlayjectorService] Unregistered android stream "${streamId}" — its notifications now drop`);
    }
  }

  /**
   * Restart a single game instance (`processRestartInstanceMSG`); sibling
   * instances are untouched. Resolves with the reply's `result` string;
   * rejects when Playjector is unavailable, when a restart for the same
   * (streamId, gameInstanceIndex) pair is already in flight, or when no reply
   * arrives within `RESTART_INSTANCE_REPLY_TIMEOUT_MS`.
   */
  public async restartInstance(streamId: string, gameInstanceIndex: number): Promise<string> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    const key = `${streamId}:${gameInstanceIndex}`;
    if (this.pendingInstanceRestarts.has(key)) {
      throw new Error(`A restart for instance ${key} is already in flight`);
    }

    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingInstanceRestarts.delete(key);
        reject(new Error(
          `Restart instance reply timed out after ${RESTART_INSTANCE_REPLY_TIMEOUT_MS}ms ` +
          `(stream ${streamId}, instance ${gameInstanceIndex})`,
        ));
      }, RESTART_INSTANCE_REPLY_TIMEOUT_MS);
      this.pendingInstanceRestarts.set(key, { resolve, reject, timeoutId });

      console.log('[PlayjectorService] 🔄 Restarting android instance:', { streamId, gameInstanceIndex });
      ipcSend(
        generators.process.processRestartInstance(
          authState.currentState.hostAuthId,
          streamId,
          gameInstanceIndex,
        ),
      );
    });
  }

  /**
   * Resolve the in-flight `restartInstance` wait for a
   * `processRestartInstanceReplyMSG` payload. Called from the renderer-side IPC
   * tap (PlayjectorService constructor) when Electron forwards the reply.
   */
  public notifyRestartInstanceReply(reply: {
    streamId: string;
    gameInstanceIndex: number;
    result: string;
  }): void {
    const key = `${reply.streamId}:${reply.gameInstanceIndex}`;
    const waiter = this.pendingInstanceRestarts.get(key);
    if (!waiter) {
      console.warn(`[PlayjectorService] Restart instance reply for ${key} with no pending request — dropping`);
      return;
    }
    this.pendingInstanceRestarts.delete(key);
    clearTimeout(waiter.timeoutId);
    waiter.resolve(reply.result);
  }

  /**
   * Subscribe to per-instance launch notifications
   * (`processInstanceLaunchedMSG`). Only notifications for streamIds
   * registered via `launchAndroidInstances` are forwarded. Returns an
   * unsubscribe function.
   */
  public onInstanceLaunched(callback: (event: InstanceLaunchedEvent) => void): () => void {
    this.instanceLaunchedListeners.push(callback);
    return () => {
      const index = this.instanceLaunchedListeners.indexOf(callback);
      if (index > -1) this.instanceLaunchedListeners.splice(index, 1);
    };
  }

  /**
   * Subscribe to per-instance closed notifications
   * (`processInstanceClosedMSG`). The match lifecycle consumes these via
   * `coordinators/init.ts` (Phase 4). Returns an unsubscribe function.
   */
  public onInstanceClosed(callback: (event: InstanceClosedEvent) => void): () => void {
    this.instanceClosedListeners.push(callback);
    return () => {
      const index = this.instanceClosedListeners.indexOf(callback);
      if (index > -1) this.instanceClosedListeners.splice(index, 1);
    };
  }

  /** Route an `instanceLaunched` mainMessage (service tap) to subscribers. */
  public routeInstanceLaunched(event: InstanceLaunchedEvent): void {
    if (!this.registeredAndroidStreamIds.has(event.streamId)) {
      console.warn(
        `[PlayjectorService] instanceLaunched for unregistered stream "${event.streamId}" ` +
        `(instance ${event.gameInstanceIndex}) — dropping`,
      );
      return;
    }
    this.instanceLaunchedListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('[PlayjectorService] Instance launched listener error:', error);
      }
    });
  }

  /** Route an `instanceClosed` mainMessage (service tap) to subscribers. */
  public routeInstanceClosed(event: InstanceClosedEvent): void {
    if (!this.registeredAndroidStreamIds.has(event.streamId)) {
      console.warn(
        `[PlayjectorService] instanceClosed for unregistered stream "${event.streamId}" ` +
        `(instance ${event.gameInstanceIndex}) — dropping`,
      );
      return;
    }
    this.instanceClosedListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('[PlayjectorService] Instance closed listener error:', error);
      }
    });
  }
}
